import { PaymentFlowContext, PaymentFlowResult } from '@/Services/PaymentMethod/types';
import { PaymentIntentResult, SetupIntent } from '@stripe/stripe-js';
import processCardPayment from '@/Services/PaymentMethod/card';
import processWalletPayment from '@/Services/PaymentMethod/wallet';
import processBankTransferPayment from '@/Services/PaymentMethod/bankTransfer';
import axios from 'axios';
import AppConfig from '@/AppConfig';
import * as Sentry from '@sentry/vue';
import processAsyncPayment from '@/Services/PaymentMethod/async';
import Authenticator from '@/Services/Auth/Authenticator';
import { mapTaxLocationValidationError } from '@/Services/PaymentMethod/taxLocationError';

const asyncPaymentMethods = ['paypal', 'klarna', 'pix'];

/**
 * Main entry point for processing payments via Payment Elements.
 *
 * @param context - The payment flow context
 * @returns PaymentFlowResult indicating success or error
 */
export async function processPayment(context: PaymentFlowContext): Promise<PaymentFlowResult> {
  if (asyncPaymentMethods.includes(context.selectedPaymentMethodType)) {
    return await processAsyncPayment(context);
  }

  switch (context.selectedPaymentMethodType) {
    case 'card':
      return await processCardPayment(context);

    case 'google_pay':
      return await processWalletPayment(context);

    case 'apple_pay':
      return await processWalletPayment(context);

    case 'bank_transfer':
      return await processBankTransferPayment(context);

    default:
      // Handle unknown payment method types
      Sentry.captureException(new Error('unsupported payment method type was passed in payment elements processing'), {
        extra: { paymentMethodType: context.selectedPaymentMethodType },
      });
      return {
        success: false,
        error: {
          message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
          container: 'payment-element-container',
          error_code: 'ERR-106',
        },
      };
  }
}

export async function processSetupIntentConfirmation(
  context: PaymentFlowContext,
  setupIntent: SetupIntent | undefined,
  error: any,
  paymentMethodType: string
): Promise<PaymentFlowResult> {
  if (error) {
    if (error.type === 'card_error' || error.type === 'validation_error') {
      // Check if card is reported lost or stolen and force logout the user
      if (error.decline_code && ['lost_card', 'stolen_card'].includes(error.decline_code)) {
        await lockUserAccount(error.decline_code);

        await Authenticator.logout();
      }

      return {
        success: false,
        error: {
          // error.code indicates an error from stripe
          // error.decline_code takes precedence over error.code
          message_key: error.decline_code
            ? `checkout.form.stripe_errors.${error.decline_code}`
            : error.code
            ? `checkout.form.stripe_errors.${error.code}`
            : 'checkout.checkout_errors.unexpected_payment_processing_error',
          container: 'payment-element-container',
        },
      };
    }

    Sentry.captureException(error);

    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-107',
      },
    };
  }

  if (!setupIntent) {
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-108',
      },
    };
  }

  if (setupIntent.status === 'succeeded') {
    return await createOrUpgradeSubscriptionFromSetupIntent(context, setupIntent, paymentMethodType);
  } else if (setupIntent.status === 'requires_action') {
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-109',
      },
    };
  }

  Sentry.captureException(new Error('unexpected state from Stripe setup intent- breaking checkout flow'), {
    extra: { setupIntentId: setupIntent.id },
  });

  return {
    success: false,
    error: {
      message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
      container: 'payment-element-container',
      error_code: 'ERR-110',
    },
  };
}

/**
 * Creates or upgrades a subscription after a successful SetupIntent.
 *
 * @param context - The payment flow context
 * @param setupIntent - The successful SetupIntent from Stripe
 * @param paymentMethodType - The type of payment method used (card, paypal, wallet, etc.)
 */
async function createOrUpgradeSubscriptionFromSetupIntent(
  context: PaymentFlowContext,
  setupIntent: SetupIntent,
  paymentMethodType: string
): Promise<PaymentFlowResult> {
  try {
    // Save the payment method and create customer in Stripe
    await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods', {
      new_payment_method_id: setupIntent.payment_method,
      legal_entity: context.stripeCheckoutService.legalEntity,
      customer: context.stripeCheckoutService.customer,
    });

    // Normalize payment method type for backend
    // Backend expects 'card' for wallet payments (google_pay, apple_pay) since they're card-based
    const backendPaymentMethodType = paymentMethodType === 'wallet' ? 'card' : paymentMethodType;
    const isWallet = paymentMethodType === 'wallet';

    // for downgrades, we just go to success page
    if (context.stripeSubscriptionService.isPlanUpcoming(context.selectedPlan.id)) {
      await redirectToPaymentComplete(context);

      return { success: true };
    }

    // Create or upgrade subscription directly
    const stripeSubService = context.stripeSubscriptionService;
    const isUpgrade = !!stripeSubService.subscription?.id;

    // Use PATCH for upgrades, POST for new subscriptions
    const response = await axios({
      method: isUpgrade ? 'patch' : 'post',
      url: AppConfig.getAPIBaseUrl() + '/subscription/stripe/' + (isUpgrade ? context.selectedPlan.id : ''),
      data: {
        plan_id: context.selectedPlan.id,
        payment_method_type: backendPaymentMethodType,
        is_wallet: isWallet,
      },
    });

    if (paymentMethodType === 'card') {
      const confirmPaymentResult: PaymentIntentResult = await context.stripeService.stripe.confirmCardPayment(
        response.data.client_secret
      );

      if (confirmPaymentResult.error) {
        // Check if card is reported lost or stolen and force logout the user
        if (
          confirmPaymentResult.error.decline_code &&
          ['lost_card', 'stolen_card'].includes(confirmPaymentResult.error.decline_code)
        ) {
          await lockUserAccount(confirmPaymentResult.error.decline_code);

          await Authenticator.logout();
        }

        let errorMessageKey = '';
        let extraData = {};

        if (confirmPaymentResult.error.decline_code) {
          errorMessageKey = 'checkout.form.stripe_errors.' + confirmPaymentResult.error.decline_code;
          extraData = { decline_code: confirmPaymentResult.error.decline_code };
        } else if (confirmPaymentResult.error.code) {
          errorMessageKey = 'checkout.form.stripe_errors.' + confirmPaymentResult.error.code;
          extraData = { code: confirmPaymentResult.error.code };
        } else {
          errorMessageKey = 'checkout.checkout_errors.unexpected_payment_processing_error';
        }

        Sentry.withScope((scope) => {
          scope.setExtras(extraData);
          Sentry.captureMessage('[PaymentForm] Payment Method declined by Stripe');
        });

        return {
          success: false,
          error: {
            message_key: errorMessageKey,
            container: 'payment-element-container',
          },
        };
      }
    }

    await redirectToPaymentComplete(context);

    return { success: true };
  } catch (error) {
    // Bad billing address rejected by Stripe Tax validation — a user-fixable 422, not an exception.
    const taxLocationError = mapTaxLocationValidationError(error);
    if (taxLocationError) {
      return { success: false, error: taxLocationError };
    }

    Sentry.captureException(error);
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-111',
      },
    };
  }
}

export async function confirmSetup(
  context: PaymentFlowContext
): Promise<{ error: any; setupIntent: SetupIntent | undefined }> {
  if (!context.stripe || !context.elements) {
    return { error: { message: 'not_ready' }, setupIntent: undefined };
  }

  try {
    const returnUrl = `${window.location.origin}/processing?returningFromPaymentElements=true`;

    // Pass billing address to the confirmSetup, so AVS checks can be done by Stripe
    const billingAddress = context.stripeCheckoutService.customer?.address;

    const { error, setupIntent } = await context.stripe.confirmSetup({
      elements: context.elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            name: context.stripeCheckoutService.customer?.name,
            email: context.stripeCheckoutService.customer?.email,
            address: {
              line1: billingAddress?.line1,
              line2: billingAddress?.line2,
              city: billingAddress?.city,
              state: billingAddress?.state,
              postal_code: billingAddress?.postal_code,
              country: billingAddress?.country,
            },
          },
        },
      },
      redirect: 'if_required',
    });

    return { error, setupIntent };
  } catch (error: any) {
    Sentry.captureException(error);
    return { error, setupIntent: undefined };
  }
}

export async function redirectToPaymentComplete(context: PaymentFlowContext): Promise<void> {
  await context.router.push({
    name: 'checkout_payment_complete',
    params: { type: context.checkoutType },
    query: { plan: context.selectedPlan.planName.toLowerCase() },
  });
}

export async function lockUserAccount(declineCode: string): Promise<void> {
  await axios.post(AppConfig.getAPIBaseUrl() + '/account/lock', {
    code: declineCode,
  });
}
